import { Component, ViewChild, ElementRef, AfterViewChecked, OnInit, ChangeDetectorRef } from '@angular/core';
import { OpenaiService } from '../../services/openai.service';
import { ImageGenerationService } from '../../services/image-generation.service';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MsalService } from '@azure/msal-angular';
import { HttpClient } from '@angular/common/http';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  type: 'text' | 'image';
  imageUrl?: string;
  timestamp: Date;
}

@Component({
    selector: 'app-chat',
    templateUrl: './chat.component.html',
    styleUrls: ['./chat.component.scss'],
    imports: [CommonModule, FormsModule]
})
export class ChatComponent implements AfterViewChecked, OnInit {
  isPanelActive = false;
  @ViewChild('chatContainer') chatContainer!: ElementRef;
  @ViewChild('chatTextarea') chatTextarea!: ElementRef<HTMLTextAreaElement>;
  
  userInput: string = '';
  messages: Message[] = [];
  isLoading: boolean = false;
  userProfilePicture: string | null = null;
  defaultProfileImage: string = 'assets/img/user.png';
  
  // Model selection
  models = [
    { slug: 'gpt-4o-mini', name: 'GPT-4o-Mini' },
    { slug: 'gpt-4.1', name: 'GPT-4.1' },
    { slug: 'gpt-5-mini', name: 'GPT-5 Mini' },
    { slug: 'gemini-2.5-flash', name: 'Gemini 2.5 Flash' },
    { slug: 'gemini-2.5-pro', name: 'Gemini 2.5 Pro' },
    { slug: 'dall-e-3', name: 'DALL-E 3 (Image Generation)' }
  ];
  selectedModel: string = 'gpt-4.1';
  
  // Image generation options (shown only when DALL-E is selected)
  imageSize: string = '1024x1024';
  imageQuality: string = 'standard';
  imageStyle: string = 'vivid';
  
  imageSizes = [
    { value: '1024x1024', label: 'Square (1024×1024)' },
    { value: '1792x1024', label: 'Landscape (1792×1024)' },
    { value: '1024x1792', label: 'Portrait (1024×1792)' }
  ];
  
  imageQualities = [
    { value: 'standard', label: 'Standard' },
    { value: 'hd', label: 'HD' }
  ];
  
  imageStyles = [
    { value: 'vivid', label: 'Vivid' },
    { value: 'natural', label: 'Natural' }
  ];

  constructor(
    private openaiService: OpenaiService,
    private imageGenerationService: ImageGenerationService,
    private msalService: MsalService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) {}
  
  ngOnInit(): void {
    this.fetchUserProfilePicture();
  }
  
  fetchUserProfilePicture(): void {
    const account = this.msalService.instance.getActiveAccount();
    if (!account) return;

    // Request token for Microsoft Graph
    this.msalService.acquireTokenSilent({
      scopes: ['User.Read'],
      account: account
    }).subscribe({
      next: (result) => {
        // Fetch profile photo from Microsoft Graph
        this.http.get('https://graph.microsoft.com/v1.0/me/photo/$value', {
          headers: { Authorization: `Bearer ${result.accessToken}` },
          responseType: 'blob'
        }).subscribe({
          next: (blob) => {
            const reader = new FileReader();
            reader.onload = () => {
              this.userProfilePicture = reader.result as string;
              this.cdr.detectChanges();
            };
            reader.readAsDataURL(blob);
          },
          error: () => {
            // No profile picture available, will use default image
            this.userProfilePicture = null;
          }
        });
      },
      error: () => {
        // Token acquisition failed, will use default image
        this.userProfilePicture = null;
      }
    });
  }
  
  getProfileImageUrl(): string {
    return this.userProfilePicture || this.defaultProfileImage;
  }
  
  // Check if current model is DALL-E
  get isDallESelected(): boolean {
    return this.selectedModel === 'dall-e-3';
  }

  togglePanel(): void {
    this.isPanelActive = !this.isPanelActive;
  }

  onModelChange(): void {
    // Optional: Add any logic when model changes
    console.log('Model changed to:', this.selectedModel);
  }

  async sendMessage(): Promise<void> {
    if (!this.userInput.trim() || this.isLoading) {
      return;
    }

    const userMessage = this.userInput.trim();
    const isImageGeneration = this.isDallESelected;
    
    // Add user message to chat
    this.messages.push({
      role: 'user',
      content: userMessage,
      type: isImageGeneration ? 'image' : 'text',
      timestamp: new Date()
    });

    this.userInput = '';
    this.resetTextareaHeight();
    this.isLoading = true;

    try {
      if (isImageGeneration) {
        await this.generateImage(userMessage);
      } else {
        await this.generateText(userMessage);
      }
    } catch (error) {
      console.error('Error:', error);
      this.messages.push({
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        type: 'text',
        timestamp: new Date()
      });
    } finally {
      this.isLoading = false;
    }
  }

  async generateImage(prompt: string): Promise<void> {
    try {
      const imageUrl = await this.imageGenerationService.generateImage(
        prompt,
        this.imageSize,
        this.imageQuality,
        this.imageStyle
      );

      this.messages.push({
        role: 'assistant',
        content: 'Here is your generated image:',
        type: 'image',
        imageUrl: imageUrl,
        timestamp: new Date()
      });
    } catch (error: any) {
      throw error;
    }
  }

  async generateText(prompt: string): Promise<void> {
    // TODO: Implement text chat using your existing OpenAI service
    // For now, just adding a placeholder response
    setTimeout(() => {
      this.messages.push({
        role: 'assistant',
        content: 'Text chat integration pending. Please implement using your OpenAI service.',
        type: 'text',
        timestamp: new Date()
      });
    }, 1000);
  }

  clearChat(): void {
    this.messages = [];
    this.userInput = '';
  }

  ngAfterViewChecked() {
    // Auto scroll to the bottom after each change in the view
    this.scrollToBottom();
  }

  scrollToBottom(): void {
    try {
      const el = this.chatContainer?.nativeElement as HTMLElement;
      if (!el) return;

      // Auto-scroll to bottom
      setTimeout(() => {
        el.scrollTo({
          top: el.scrollHeight,
          behavior: 'smooth'
        });
      }, 0);
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  // Auto-resize textarea as user types
  autoResize(event: Event): void {
    const textarea = event.target as HTMLTextAreaElement;
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    // Calculate new height (limited by max-height in CSS)
    const maxHeight = 150; // matches max-height in CSS
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);
    textarea.style.height = newHeight + 'px';
  }

  // Reset textarea height after sending message
  resetTextareaHeight(): void {
    if (this.chatTextarea?.nativeElement) {
      this.chatTextarea.nativeElement.style.height = 'auto';
    }
  }

  copyMessage(content: string): void {
    navigator.clipboard.writeText(content).then(() => {
      // Optional: could add a toast notification here
    });
  }
}
